import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail, Check, Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  ['auslaenderrecht', 'Ausländerrecht'],
  ['strafrecht', 'Strafrecht'],
  ['steuerberatung', 'Steuerberatung'],
  ['steuerrecht', 'Steuerrecht'],
  ['gesellschaftsrecht', 'Gesellschaftsrecht'],
  ['verwaltungsrecht', 'Verwaltungsrecht'],
  ['mietrecht', 'Mietrecht'],
  ['verkehrsrecht', 'Verkehrsrecht'],
];

const NewsletterBlock = ({ activeArea = '' }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState(activeArea ? [activeArea] : []);
  const [state, setState] = useState('idle'); // idle | loading | success | error | already
  const [error, setError] = useState('');

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const toggle = (slug) =>
    setSelected((cur) => (cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug]));

  const submit = async (e) => {
    e.preventDefault();
    if (!validEmail(email)) {
      setState('error');
      setError(t('newsletter.error_invalid', 'Bitte geben Sie eine gültige E-Mail-Adresse an.'));
      return;
    }
    setState('loading');
    setError('');
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        rechtsgebiete: selected,
        lang: i18n.language || 'de',
      });
      if (res.data?.status === 'already_confirmed') {
        setState('already');
      } else {
        setState('success');
      }
    } catch (err) {
      setState('error');
      setError(err?.response?.data?.detail || t('newsletter.error_generic', 'Die Anmeldung ist fehlgeschlagen.'));
    }
  };

  if (state === 'success') {
    return (
      <section className="mt-16 bg-[#1B3A5C] text-white p-8 md:p-10 rounded-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#b8893d] rounded-sm flex items-center justify-center flex-shrink-0">
            <Check className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-serif text-2xl mb-2">{t('newsletter.success_title', 'Fast geschafft!')}</h3>
            <p className="text-white/80 leading-relaxed">
              {t('newsletter.success_body', 'Wir haben Ihnen eine Bestätigungs-E-Mail an {{email}} geschickt.', { email })}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (state === 'already') {
    return (
      <section className="mt-16 bg-[#f5f8fa] border-l-4 border-[#b8893d] p-6 md:p-7 rounded-sm">
        <div className="flex items-center gap-3 text-[#1B3A5C]">
          <Check className="w-5 h-5 text-[#b8893d]" />
          <p>{t('newsletter.already_confirmed', 'Diese E-Mail-Adresse ist bereits für unseren Newsletter angemeldet.')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-16 bg-[#1B3A5C] text-white rounded-sm overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-5">
        <div className="md:col-span-2 bg-[#0f2740] p-8 md:p-10 flex flex-col justify-center">
          <p className="text-[#e0b56a] text-xs tracking-[0.3em] mb-3">{t('newsletter.eyebrow', 'NEWSLETTER')}</p>
          <h2 className="font-serif font-light text-3xl md:text-4xl mb-4 leading-tight">
            {t('newsletter.headline', 'Bleiben Sie informiert')}
          </h2>
          <p className="text-white/80 text-sm leading-relaxed">
            {t('newsletter.subline', 'Erhalten Sie regelmäßig kuratierte Updates aus BGH, BFH und BVerwG – sortiert nach Ihren Rechtsgebieten. Kostenlos, jederzeit abbestellbar.')}
          </p>
        </div>

        <form onSubmit={submit} className="md:col-span-3 p-8 md:p-10 space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('newsletter.placeholder', 'Ihre E-Mail-Adresse')}
              data-testid="newsletter-email"
              className="w-full pl-10 pr-3 py-3 bg-white text-[#1B3A5C] rounded-sm placeholder:text-[#34536b]/60 focus:outline-none focus:ring-2 focus:ring-[#b8893d]/50"
            />
          </div>

          <div>
            <p className="text-white/60 text-xs tracking-wider mb-2 uppercase">
              {t('newsletter.select_areas', 'Optional: Themen einschränken')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {AREAS.map(([slug, label]) => {
                const isActive = selected.includes(slug);
                return (
                  <button
                    key={slug}
                    type="button"
                    data-testid={`newsletter-rg-${slug}`}
                    onClick={() => toggle(slug)}
                    className={`text-[11px] tracking-wider px-2.5 py-1 border transition-colors ${
                      isActive
                        ? 'bg-[#b8893d] text-white border-[#b8893d]'
                        : 'bg-transparent text-white/80 border-white/30 hover:border-[#b8893d]'
                    }`}
                  >
                    {label.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {state === 'error' && (
            <div className="flex items-center gap-2 text-red-300 text-sm" role="alert">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={state === 'loading'}
            data-testid="newsletter-submit"
            className="bg-[#b8893d] hover:bg-[#a07730] disabled:opacity-60 text-white px-7 py-3 tracking-[0.15em] text-xs inline-flex items-center gap-2 transition-colors"
          >
            {state === 'loading' ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('newsletter.submitting', 'Wird gesendet…')}
              </>
            ) : (
              t('newsletter.submit', 'ABONNIEREN')
            )}
          </button>

          <p className="text-white/50 text-[11px] leading-relaxed pt-2">
            {t('newsletter.privacy_notice', 'Mit dem Klick auf ABONNIEREN willigen Sie ein, dass wir Ihre E-Mail-Adresse zum Versand des Newsletters verarbeiten. Sie können sich jederzeit über den Link in jeder Newsletter-E-Mail abmelden. Details in unserer Datenschutzerklärung.')}{' '}
            <Link to="/datenschutz" className="text-[#e0b56a] underline">Datenschutz</Link>.
          </p>

          <p className="text-white/70 text-[12px] pt-1">
            <a
              href={`${API}/newsletter/preview`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="newsletter-preview-link"
              className="inline-flex items-center gap-1.5 text-[#e0b56a] hover:text-white underline-offset-4 hover:underline transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              {t('newsletter.preview_link', 'Vorschau ansehen – so sieht der wöchentliche Digest aus')}
            </a>
          </p>
        </form>
      </div>
    </section>
  );
};

export default NewsletterBlock;
